import React, { Fragment, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';

/**
 * Drawer — Side-sheet with pre-selected context (REQ-SYS-FND-01)
 *
 * Carries context data in (e.g., customer data when opening add-quote).
 * Focus-trapped, Esc to close, scrim dims background.
 *
 * Usage:
 *   <Drawer
 *     open={isOpen}
 *     onClose={() => setOpen(false)}
 *     title="Add Quote"
 *     context={{ customer: selectedCustomer }}
 *     size="lg"
 *   >
 *     <QuoteBuilderPage customer={selectedCustomer} />
 *   </Drawer>
 */
export default function Drawer({
  open = false,
  onClose,
  title,
  subtitle,
  context,
  children,
  size = 'md',
  side = 'right',
  footer,
  className,
}) {
  // Esc key handling (Headless UI Dialog handles this automatically)
  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    full: 'max-w-full',
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        {/* Scrim */}
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className={clsx(
              'pointer-events-none fixed inset-y-0 flex',
              side === 'right' ? 'right-0 pl-10' : 'left-0 pr-10'
            )}>
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom={side === 'right' ? 'translate-x-full' : '-translate-x-full'}
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-200"
                leaveFrom="translate-x-0"
                leaveTo={side === 'right' ? 'translate-x-full' : '-translate-x-full'}
              >
                <Dialog.Panel
                  className={clsx(
                    'pointer-events-auto w-screen',
                    sizeClasses[size] || sizeClasses.md,
                    className
                  )}
                >
                  <div
                    className="flex h-full flex-col overflow-y-auto"
                    style={{ background: 'var(--color-surface)' }}
                  >
                    {/* Header */}
                    <header
                      className="flex items-start justify-between gap-3 px-5 py-4 border-b shrink-0"
                      style={{ borderColor: 'var(--color-border)' }}
                    >
                      <div>
                        <Dialog.Title
                          className="text-base font-semibold"
                          style={{ color: 'var(--color-text-default)' }}
                        >
                          {title}
                        </Dialog.Title>
                        {subtitle && (
                          <p className="text-sm mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                            {subtitle}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={handleClose}
                        className="p-1 hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-action-primary)]"
                        style={{ borderRadius: 'var(--radius-md)' }}
                      >
                        <XMarkIcon className="h-5 w-5" style={{ color: 'var(--color-text-muted)' }} />
                      </button>
                    </header>

                    {/* Body */}
                    <div className="flex-1 overflow-y-auto px-5 py-4">
                      {typeof children === 'function' ? children({ context }) : children}
                    </div>

                    {/* Footer */}
                    {footer && (
                      <footer
                        className="shrink-0 px-5 py-3 border-t"
                        style={{ borderColor: 'var(--color-border)' }}
                      >
                        {footer}
                      </footer>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
